import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Gift, Calculator } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";

const ANY_VALUE = "__ANY__";

export default function OpsBulkGift() {
  const { user } = useAuth();
  const writable = canWrite(user?.staffRole);

  const [city, setCity] = useState("");
  const [specialty, setSpecialty] = useState<string>(ANY_VALUE);
  const [tagId, setTagId] = useState<string>(ANY_VALUE);
  const [amount, setAmount] = useState<string>("5");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const tagsQuery = trpc.admin.opsTags.listArtisanTags.useQuery(undefined, {
    staleTime: 60_000,
  });

  // Try to load specialty list from existing taxonomy if available; fallback empty.
  const specialtiesQuery = trpc.admin.categories.list.useQuery(
    { active: true },
    { staleTime: 60_000 }
  );

  const filters = {
    city: city.trim() || undefined,
    specialty: specialty !== ANY_VALUE ? specialty : undefined,
    artisanTagId: tagId !== ANY_VALUE ? tagId : undefined,
  };

  const estimateQuery = trpc.admin.opsTags.estimateBulkGiftRecipients.useQuery(
    filters,
    { enabled: false }
  );

  const bulkMutation = trpc.admin.opsTags.bulkGiftCredits.useMutation({
    onSuccess: (res) => {
      toast.success(
        `Cadeau envoyé à ${res.recipientCount} entreprises (${res.totalCreditsGiven} crédits)`
      );
      setConfirmOpen(false);
      setReason("");
    },
    onError: (e) => toast.error(e.message),
  });

  const numericAmount = Number.parseInt(amount, 10);
  const amountValid = Number.isInteger(numericAmount) && numericAmount > 0;
  const reasonValid = reason.trim().length >= 3;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gift className="h-6 w-6" />
            Cadeau de crédits en masse
          </h1>
          <p className="text-sm text-muted-foreground">
            Offrez des crédits à un segment d'artisans vérifiés (max 500 destinataires).
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Filtres destinataires</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="bg-city">Ville</Label>
                <Input
                  id="bg-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Paris, Lyon…"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Spécialité</Label>
                <Select value={specialty} onValueChange={setSpecialty}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_VALUE}>Toutes</SelectItem>
                    {(specialtiesQuery.data ?? [])
                      .map((c: any) => ({ ...c, _value: c.code || c.label || c.id }))
                      .filter((c: any) => c._value)
                      .map((c: any) => (
                        <SelectItem key={c.id} value={c._value}>
                          {c.label ?? c.code}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tag artisan</Label>
                <Select value={tagId} onValueChange={setTagId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY_VALUE}>Tous</SelectItem>
                    {(tagsQuery.data ?? []).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="bg-amount">Crédits par artisan</Label>
                <Input
                  id="bg-amount"
                  type="number"
                  min={1}
                  max={1000}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bg-reason">Raison (visible dans l'audit)</Label>
                <Input
                  id="bg-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Compensation incident 14/05"
                  maxLength={280}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => estimateQuery.refetch()}
                disabled={estimateQuery.isFetching}
              >
                <Calculator className="h-4 w-4 mr-1" />
                Estimer destinataires
              </Button>
              {estimateQuery.data && (
                <span className="text-sm">
                  <strong>{estimateQuery.data.recipientCount}</strong>{" "}
                  artisan(s) vérifié(s) — limite{" "}
                  {estimateQuery.data.maxRecipients}
                  {estimateQuery.data.recipientCount >
                    estimateQuery.data.maxRecipients && (
                    <span className="text-destructive ml-2">
                      (au-dessus de la limite, affinez les filtres)
                    </span>
                  )}
                </span>
              )}
              <div className="flex-1" />
              {writable && (
                <Button
                  type="button"
                  onClick={() => setConfirmOpen(true)}
                  disabled={!amountValid || !reasonValid}
                >
                  <Gift className="h-4 w-4 mr-1" />
                  Confirmer & envoyer
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer le cadeau de crédits</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              <strong>{numericAmount}</strong> crédits seront ajoutés à chaque
              artisan vérifié correspondant aux filtres :
            </p>
            <ul className="list-disc pl-5 text-muted-foreground">
              <li>Ville : {filters.city ?? "—"}</li>
              <li>Spécialité : {filters.specialty ?? "—"}</li>
              <li>
                Tag :{" "}
                {filters.artisanTagId
                  ? tagsQuery.data?.find((t) => t.id === filters.artisanTagId)
                      ?.name ?? filters.artisanTagId
                  : "—"}
              </li>
              <li>Raison : {reason.trim()}</li>
            </ul>
            {estimateQuery.data && (
              <p className="pt-1">
                Destinataires estimés :{" "}
                <strong>{estimateQuery.data.recipientCount}</strong>
              </p>
            )}
            <Textarea
              className="hidden"
              value={reason}
              onChange={() => undefined}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Annuler
            </Button>
            <Button
              disabled={bulkMutation.isPending || !amountValid || !reasonValid}
              onClick={() =>
                bulkMutation.mutate({
                  filters,
                  amount: numericAmount,
                  reason: reason.trim(),
                })
              }
            >
              Envoyer les crédits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
